import type { Font } from "fontkit";
export type {
  FontFaceDescriptor,
  FontRuntime,
  FontRuntimeMap,
} from "@proof/core";
import {
  verifyFontBytes,
  type FontFaceDescriptor,
  type FontRuntime,
  type FontRuntimeMap,
  fontCssFamily,
  openTypeFeatures,
  kerningPairDeltas,
  type TextFeatures,
} from "@proof/core";
function fontRuntime(font: Font, faceId: string): FontRuntime {
  const shape = (text: string, features?: TextFeatures) => {
    const run = font.layout(text, openTypeFeatures(features));
    const deltas = kerningPairDeltas(run.glyphs, features, faceId);
    if (deltas.length)
      run.positions = run.positions.map((position, i) => ({
        ...position,
        xAdvance: position.xAdvance + (deltas[i] * font.unitsPerEm) / 1000,
      }));
    return run;
  };
  return {
    ascent: font.ascent / font.unitsPerEm,
    underlinePosition: font.underlinePosition / font.unitsPerEm,
    underlineThickness: font.underlineThickness / font.unitsPerEm,
    hasGlyph: (cp) => font.hasGlyphForCodePoint(cp),
    measure: (text, features) => {
      for (const c of text)
        if (
          !/[\s\u200c\u200d\ufe00-\ufe0f]/u.test(c) &&
          !font.hasGlyphForCodePoint(c.codePointAt(0)!)
        )
          throw new Error(
            `Font ${font.fullName} has no glyph for U+${c.codePointAt(0)!.toString(16).toUpperCase()}`,
          );
      return (
        shape(text, features).positions.reduce(
          (width, p) => width + p.xAdvance,
          0,
        ) / font.unitsPerEm
      );
    },
    outline: (text, features) => {
      if (text.length > 32768)
        throw new Error("Text run exceeds outline budget");
      const shaped = shape(text, features);
      let x = 0,
        y = 0;
      return shaped.glyphs.map((glyph, i) => {
        const p = shaped.positions[i],
          result = {
            path: glyph.path.toSVG(),
            x: (x + p.xOffset) / font.unitsPerEm,
            y: (y + p.yOffset) / font.unitsPerEm,
            scale: 1 / font.unitsPerEm,
          };
        x += p.xAdvance;
        y += p.yAdvance;
        return result;
      });
    },
  };
}
export async function decodeRuntime(
  face: FontFaceDescriptor,
  bytes: Uint8Array,
) {
  verifyFontBytes(face, bytes);
  const { create } = await import("fontkit");
  const font = create(bytes as Buffer) as Font;
  if (!("unitsPerEm" in font) || !font.unitsPerEm)
    throw new Error(`Invalid font face: ${face.name}`);
  return fontRuntime(font, face.id);
}
export type FontLoader = (
  face: FontFaceDescriptor,
  signal?: AbortSignal,
) => Promise<Uint8Array>;
export const defaultFontLoader: FontLoader = async (face, signal) => {
  const response = await fetch(
    `/api/fonts/${encodeURIComponent(face.id)}/file`,
    { signal },
  );
  if (!response.ok) throw new Error(`Font unavailable: ${face.name}`);
  return new Uint8Array(await response.arrayBuffer());
};
export type LoadedBrowserFonts = FontRuntimeMap & { dispose: () => void };
export async function loadBrowserFonts(
  manifest: FontFaceDescriptor[],
  loader: FontLoader = defaultFontLoader,
  signal?: AbortSignal,
): Promise<LoadedBrowserFonts> {
  const fonts = new Map<string, FontRuntime>(),
    faces: FontFace[] = [];
  const dispose = () => {
    for (const face of faces) document.fonts.delete(face);
    faces.length = 0;
  };
  try {
    for (const face of manifest) {
      if (signal?.aborted)
        throw new DOMException("Font loading cancelled", "AbortError");
      const bytes = await loader(face, signal);
      verifyFontBytes(face, bytes);
      const runtime = await decodeRuntime(face, bytes);
      const browserFace = new FontFace(
        fontCssFamily(face),
        new Uint8Array(bytes).buffer as ArrayBuffer,
        { weight: String(face.weight), style: face.style },
      );
      await browserFace.load();
      if (signal?.aborted)
        throw new DOMException("Font loading cancelled", "AbortError");
      document.fonts.add(browserFace);
      faces.push(browserFace);
      fonts.set(face.id, runtime);
    }
    return Object.assign(fonts, { dispose });
  } catch (error) {
    dispose();
    throw error;
  }
}
